import './polyfills';
import './style.css';
import { ApiClient } from './api/client';
import { Controller } from './controller';
import { initialState, Store } from './state/store';
import { render } from './ui/render';
import { viewFromPath } from './routing';
import {
  attack,
  descend,
  discard,
  equipFound,
  extract,
  move,
  pickup,
  useConsumable,
  wait,
  type Direction,
} from './game/runActions';
import type { EquipSlot } from './api/types';
import { BrowserGameFeel } from './feel/browserGameFeel';
import {
  updateFreeSpinCountdowns,
  updateSpinnerQuoteCountdowns,
} from './ui/spinnerView';
import {
  updateFerryPassCountdowns,
} from './ui/dashboard';
import { updateExpiryCountdowns } from './ui/format';
import { CloudflareTurnstileProvider, NoopTurnstileProvider } from './turnstile/provider';
import { getPhantomProvider } from './auth/wallet';
import { WALLET_CHANGED_MESSAGE, WalletWatcher } from './auth/walletWatcher';
import { copyContractAddress, showToast } from './ui/toast';
import {
  drawWaystationPlaza,
  hubCanvasClickTarget,
  resetRemotePresenceRenderState,
  stationById,
  type HubDirection,
} from './ui/waystationPlaza';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080';
const DEV_MODE = import.meta.env.DEV;
// PUBLIC Cloudflare Turnstile site key (Cloudflare Pages env). The secret key
// is backend-only and must never appear in this bundle.
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

const root = document.querySelector<HTMLDivElement>('#app');
if (!root) throw new Error('#app root not found');
const appRoot = root;

function bindContractCopyControls(container: HTMLElement): void {
  container.addEventListener('click', (ev) => {
    // Header "Contract address" buttons: copy + transient toast feedback.
    const toastButton = (ev.target as HTMLElement).closest<HTMLButtonElement>('[data-contract-copy-toast]');
    if (toastButton) {
      const value = toastButton.dataset.copyValue;
      if (!value) return;
      ev.preventDefault();
      void copyContractAddress(value, navigator.clipboard?.writeText?.bind(navigator.clipboard));
      return;
    }

    const button = (ev.target as HTMLElement).closest<HTMLButtonElement>('[data-contract-copy]');
    if (!button) return;
    const value = button.dataset.copyValue;
    if (!value) return;

    ev.preventDefault();
    const writeText = navigator.clipboard?.writeText?.bind(navigator.clipboard);
    if (!writeText) {
      button.textContent = 'Copy unavailable';
      return;
    }

    void writeText(value).then(
      () => {
        button.dataset.copied = 'true';
        button.textContent = 'Copied';
      },
      () => {
        button.textContent = 'Copy failed';
      },
    );
  });
}

const requestedView = viewFromPath(window.location.pathname);
const store = new Store({ ...initialState(API_BASE, DEV_MODE), view: requestedView ?? 'landing' });
const client = new ApiClient({ baseUrl: API_BASE, getToken: () => store.get().session });
const gameFeel = new BrowserGameFeel(DEV_MODE);
const controller = new Controller(store, client, {
  turnstile: TURNSTILE_SITE_KEY
    ? new CloudflareTurnstileProvider(TURNSTILE_SITE_KEY)
    : new NoopTurnstileProvider(),
  onRunActionResult: (action, prev, result) => {
    gameFeel.handleActionResult(action, prev, result);
  },
  onSpectateFrame: (prev, current) => {
    gameFeel.handleSpectateFrame(prev, current);
  },
  onSpectateReset: () => {
    gameFeel.clear();
  },
});

function renderApp(): void {
  const state = store.get();
  render(appRoot, state);
  updateFreeSpinCountdowns(appRoot);
  updateSpinnerQuoteCountdowns(appRoot);
  updateFerryPassCountdowns(appRoot);
  updateExpiryCountdowns(appRoot);
  syncSpinnerCountdownLoop(state);
  syncFerryPassCountdownLoop(state);
  syncExpiryCountdownLoop(state);
  drawHubPlaza(state);
  syncHubPlazaLoop(state);
  gameFeel.afterRender(appRoot, state);
}

let hubPlazaRafId: number | null = null;
let spinnerCountdownIntervalId: number | null = null;
let ferryPassCountdownIntervalId: number | null = null;
let expiryCountdownIntervalId: number | null = null;

function drawHubPlaza(state = store.get()): void {
  if (state.view !== 'waystation' || !state.hub) return;
  const canvas = appRoot.querySelector<HTMLCanvasElement>('[data-hub-plaza-canvas]');
  if (!canvas) return;
  drawWaystationPlaza(canvas, state);
}

function hubPlazaTick(): void {
  hubPlazaRafId = null;
  const state = store.get();
  if (state.view !== 'waystation' || !state.hub) return;
  drawHubPlaza(state);
  hubPlazaRafId = window.requestAnimationFrame(hubPlazaTick);
}

function syncHubPlazaLoop(state = store.get()): void {
  const active = state.view === 'waystation' && !!state.hub;
  if (active && hubPlazaRafId === null) {
    hubPlazaRafId = window.requestAnimationFrame(hubPlazaTick);
  } else if (!active && hubPlazaRafId !== null) {
    window.cancelAnimationFrame(hubPlazaRafId);
    hubPlazaRafId = null;
    resetRemotePresenceRenderState();
  }
}

function syncSpinnerCountdownLoop(state = store.get()): void {
  const active =
    state.view === 'spinner' &&
    !!appRoot.querySelector('[data-free-spin-countdown], [data-spin-quote-countdown]');
  if (active && spinnerCountdownIntervalId === null) {
    spinnerCountdownIntervalId = window.setInterval(() => {
      updateFreeSpinCountdowns(appRoot);
      updateSpinnerQuoteCountdowns(appRoot);
    }, 1_000);
  } else if (!active && spinnerCountdownIntervalId !== null) {
    window.clearInterval(spinnerCountdownIntervalId);
    spinnerCountdownIntervalId = null;
  }
}

function syncFerryPassCountdownLoop(state = store.get()): void {
  const active = (state.view === 'dashboard' || state.view === 'waystation') && !!appRoot.querySelector('[data-ferry-pass-countdown]');
  if (active && ferryPassCountdownIntervalId === null) {
    ferryPassCountdownIntervalId = window.setInterval(() => updateFerryPassCountdowns(appRoot), 1_000);
  } else if (!active && ferryPassCountdownIntervalId !== null) {
    window.clearInterval(ferryPassCountdownIntervalId);
    ferryPassCountdownIntervalId = null;
  }
}

function syncExpiryCountdownLoop(state = store.get()): void {
  const active = state.view === 'cashout' && !!appRoot.querySelector('[data-expires-countdown]');
  if (active && expiryCountdownIntervalId === null) {
    expiryCountdownIntervalId = window.setInterval(() => updateExpiryCountdowns(appRoot), 1_000);
  } else if (!active && expiryCountdownIntervalId !== null) {
    window.clearInterval(expiryCountdownIntervalId);
    expiryCountdownIntervalId = null;
  }
}

store.subscribe(() => renderApp());
renderApp();
bindContractCopyControls(appRoot);
window.addEventListener('gravelight:hub-assets-loaded', () => drawHubPlaza());

// ── delegated click handling (data-act on buttons) ──────────────────────────
const MOVE_KEYS: Record<string, Direction> = {
  ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
  w: 'up', s: 'down', a: 'left', d: 'right',
};

const HUB_MOVE_KEYS: Record<string, HubDirection> = {
  ArrowUp: 'north', ArrowDown: 'south', ArrowLeft: 'west', ArrowRight: 'east',
  w: 'north', s: 'south', a: 'west', d: 'east',
};

appRoot.addEventListener('click', (ev) => {
  void gameFeel.unlockAudio();
  const canvas = (ev.target as HTMLElement).closest<HTMLCanvasElement>('[data-hub-plaza-canvas]');
  if (canvas) {
    const target = hubCanvasClickTarget(canvas, store.get(), ev.clientX, ev.clientY);
    if (target?.kind === 'player') {
      ev.preventDefault();
      return void controller.inspectPlayer(target.accountId);
    }
    if (target?.kind === 'station' && store.get().hubNearbyStationId === target.stationId) {
      const station = stationById(target.stationId);
      if (station) {
        ev.preventDefault();
        return void dispatch(station.primaryAct, {} as DOMStringMap);
      }
    }
  }
  const el = (ev.target as HTMLElement).closest<HTMLElement>('[data-act]');
  if (!el || el.hasAttribute('disabled')) return;
  ev.preventDefault();
  const act = el.dataset.act!;
  const ds = el.dataset;
  void dispatch(act, ds);
});

// Sort/filter dropdowns (change events don't bubble through data-act clicks).
appRoot.addEventListener('change', (ev) => {
  const target = ev.target as HTMLElement;
  const cashoutSort = target.closest<HTMLSelectElement>('[data-cashout-sort]');
  if (cashoutSort) return controller.setCashoutSort(cashoutSort.value);
  const marketRarity = target.closest<HTMLSelectElement>('[data-market-rarity]');
  if (marketRarity) return controller.setMarketRarity(marketRarity.value);
  const marketSort = target.closest<HTMLSelectElement>('[data-market-sort]');
  if (marketSort) return controller.setMarketSort(marketSort.value);
  const marketSkin = target.closest<HTMLSelectElement>('[data-market-skin]');
  if (marketSkin) return controller.setMarketSkin(marketSkin.value);
  const marketCurrency = target.closest<HTMLInputElement>('input[data-market-currency]');
  if (marketCurrency) {
    return controller.setMarketCurrency(marketCurrency.dataset.marketCurrency!, marketCurrency.checked);
  }
});

document.addEventListener('keydown', (ev) => {
  void gameFeel.unlockAudio();
  const state = store.get();
  const target = ev.target as HTMLElement | null;
  const editing = target?.matches('input, textarea, select, [contenteditable="true"]') ?? false;
  if (!editing && state.view === 'waystation' && state.hub && !state.busy) {
    if (ev.key in HUB_MOVE_KEYS) {
      ev.preventDefault();
      controller.moveHubLocal(HUB_MOVE_KEYS[ev.key]!);
      return;
    }
    if (ev.key === 'e' || ev.key === 'E') {
      const station = stationById(state.hubNearbyStationId);
      if (station) {
        ev.preventDefault();
        void dispatch(station.primaryAct, {} as DOMStringMap);
      }
      return;
    }
  }
  if (state.view !== 'run' || !state.run || state.terminal || state.busy) return;
  if (ev.key in MOVE_KEYS) {
    ev.preventDefault();
    void controller.act(move(MOVE_KEYS[ev.key]!));
  }
});

async function dispatch(act: string, ds: DOMStringMap): Promise<void> {
  switch (act) {
    // auth
    case 'connect-phantom': return controller.connectPhantom();
    case 'connect-dev': return controller.connectDevWallet();
    case 'new-dev-wallet': return controller.newDevWallet();
    case 'logout': return void controller.logout();

    // navigation
    case 'goto-landing': return void controller.showLanding();
    case 'goto-docs':
      controller.showDocs();
      return void scrollDocsTarget(ds.docsTarget);
    case 'goto-how-to-play': return void controller.showHowToPlay();
    case 'goto-roadmap': return void controller.showRoadmap();
    case 'goto-spectate': return controller.openSpectate();
    case 'goto-dashboard': return void controller.setView('dashboard');
    case 'goto-run': return void controller.setView('run');
    case 'goto-waystation': return controller.enterWaystation();
    case 'play-from-home': return controller.playFromHome();

    // spectate / non-holder funnel (Phase J)
    case 'spectate-open': return controller.openReplay(ds.run!);
    case 'spectate-close': return void controller.closeReplay();
    case 'spectate-prev': return void controller.spectateStep(-1);
    case 'spectate-next': return void controller.spectateStep(1);
    case 'spectate-reset': return void controller.spectateReset();
    case 'spectate-play': return void controller.spectateSetPlaying(true);
    case 'spectate-pause': return void controller.spectateSetPlaying(false);
    case 'goto-inventory':
      await controller.loadInventory();
      return void controller.setView('inventory');
    case 'goto-marketplace': return controller.openMarketplace();
    case 'goto-merchant': return controller.openMerchant();
    case 'goto-cashout': return controller.openCashout();
    case 'goto-spinner': return controller.openSpinner();
    case 'goto-cosmetics': return controller.openCosmetics();
    case 'goto-admin': return controller.openAdmin();

    // Admin config (Phase L1)
    case 'admin-token-save': {
      const input = appRoot.querySelector<HTMLInputElement>('input[data-admin-token]');
      return controller.setAdminTokenAndLoad(input?.value ?? '');
    }
    case 'admin-token-clear': return void controller.clearAdminToken();
    case 'admin-refresh': return controller.refreshAdminData();
    case 'admin-save': {
      const textarea = appRoot.querySelector<HTMLTextAreaElement>(`textarea[data-admin-config-key="${CSS.escape(ds.key!)}"]`);
      return controller.saveAdminConfig(ds.key!, textarea?.value ?? '');
    }
    case 'admin-abuse-refresh': return controller.refreshAdminAbuse();
    case 'admin-abuse-scan': return controller.scanAdminAbuse();
    case 'admin-abuse-open': return controller.openAdminAbuseSignal(Number(ds.id));
    case 'admin-abuse-review': return controller.reviewAdminAbuse(Number(ds.id));
    case 'admin-abuse-dismiss': return controller.dismissAdminAbuse(Number(ds.id));
    case 'admin-abuse-flag': return controller.flagAdminAbuse(Number(ds.id));
    case 'admin-ops-refresh': return controller.refreshAdminOpsSummary();
    case 'admin-replay-run': {
      const input = appRoot.querySelector<HTMLInputElement>('input[data-admin-replay-run]');
      return controller.replayAdminRun(input?.value ?? '');
    }

    // marketplace (Phase G)
    case 'market-refresh': return controller.refreshMarketplace();
    case 'market-category': return void controller.setMarketCategory(ds.category!);
    case 'market-list': {
      const currencySelect = appRoot.querySelector<HTMLSelectElement>(`select[data-currency-for="${ds.item}"]`);
      const currency = currencySelect?.value === 'obol' ? 'obol' as const : 'gold' as const;
      if (currency === 'obol') {
        const obolInput = appRoot.querySelector<HTMLInputElement>(`input[data-obol-price-for="${ds.item}"]`);
        return controller.listItem(ds.item!, { currency, obolText: obolInput?.value ?? '' });
      }
      const input = appRoot.querySelector<HTMLInputElement>(`input[data-price-for="${ds.item}"]`);
      const price = input && input.value.trim() !== '' ? Number(input.value) : NaN;
      return controller.listItem(ds.item!, { currency, price });
    }
    case 'market-cancel': return controller.cancelListing(ds.listing!);
    case 'market-buy': return controller.buyListing(ds.listing!);
    case 'market-pay-obol': return controller.payObolListing(ds.kind === 'cosmetic' ? 'cosmetic' : 'gear', ds.listing!);
    case 'market-intent-submit': {
      const input = appRoot.querySelector<HTMLInputElement>(`input[data-market-tx-for="${ds.intent}"]`);
      return controller.submitMarketIntentTx(ds.intent!, input?.value ?? '');
    }
    case 'market-intent-submit-mock': return controller.submitMarketIntentTx(ds.intent!, ds.tx!);
    case 'market-intent-retry': return controller.retryMarketIntentSettlement(ds.intent!);
    case 'market-intent-dismiss': return void controller.dismissMarketIntentPayment(ds.intent!);

    // Gold cash-out / Gold Exchange (Phase H)
    case 'cashout-refresh': return controller.refreshCashout();
    case 'cashout-create': {
      const goldInput = appRoot.querySelector<HTMLInputElement>('input[data-cashout-gold]');
      const obolInput = appRoot.querySelector<HTMLInputElement>('input[data-cashout-obol]');
      const daysInput = appRoot.querySelector<HTMLInputElement>('input[data-cashout-days]');
      const gold = goldInput && goldInput.value.trim() !== '' ? Number(goldInput.value) : NaN;
      // Human-readable whole-token $OBOL price: passed as text so the
      // controller can convert it to raw units with exact decimal math.
      const obol = obolInput?.value ?? '';
      const days = daysInput && daysInput.value.trim() !== '' ? Number(daysInput.value) : 7;
      return controller.createCashoutOrder(gold, obol, days);
    }
    case 'cashout-cancel': return controller.cancelCashoutOrder(ds.order!);
    case 'cashout-pay': return controller.payCashoutWithWallet(ds.order!);
    case 'cashout-intent-submit': {
      const input = appRoot.querySelector<HTMLInputElement>(`input[data-cashout-tx-for="${ds.intent}"]`);
      return controller.submitCashoutIntentTx(ds.intent!, input?.value ?? '');
    }
    case 'cashout-intent-submit-mock': return controller.submitCashoutIntentTx(ds.intent!, ds.tx!);
    case 'cashout-intent-retry': return controller.retryCashoutIntentSettlement(ds.intent!);
    case 'cashout-intent-dismiss': return void controller.dismissCashoutIntentPayment(ds.intent!);

    // Spinner / Ferryman's Wager (Phase I)
    case 'spinner-refresh': return controller.refreshSpinner();
    case 'spinner-free': return controller.freeSpin();
    case 'spinner-create': return controller.createSpinnerOrder();
    case 'spinner-cancel': return controller.cancelSpinnerOrder(ds.order!);
    case 'spinner-pay': return controller.paySpinnerWithWallet(ds.order!);
    case 'spinner-submit': {
      const input = appRoot.querySelector<HTMLInputElement>(`input[data-spin-tx-for="${ds.order}"]`);
      return controller.submitSpinnerTx(ds.order!, input?.value ?? '');
    }
    case 'spinner-submit-mock': return controller.submitSpinnerTx(ds.order!, ds.tx!);

    // Cosmetics (Phase K)
    case 'cosmetics-refresh': return controller.refreshCosmetics();
    case 'cosmetic-buy': return controller.buyCosmetic(ds.cosmetic!);
    case 'cosmetic-claim': return controller.claimCosmeticCampaign(ds.campaign!);
    case 'cosmetic-founder-claim': return controller.claimFounderCosmetic();
    case 'cosmetic-equip': return controller.equipCosmetic(ds.cosmetic!);
    case 'cosmetic-unequip': return controller.unequipCosmetic(ds.cosmetic!);
    case 'cosmetic-market-list': {
      const currencySelect = appRoot.querySelector<HTMLSelectElement>(`select[data-cosmetic-currency-for="${ds.cosmetic}"]`);
      const currency = currencySelect?.value === 'obol' ? 'obol' as const : 'gold' as const;
      if (currency === 'obol') {
        const obolInput = appRoot.querySelector<HTMLInputElement>(`input[data-cosmetic-obol-price-for="${ds.cosmetic}"]`);
        return controller.listCosmetic(ds.cosmetic!, { currency, obolText: obolInput?.value ?? '' });
      }
      const input = appRoot.querySelector<HTMLInputElement>(`input[data-cosmetic-price-for="${ds.cosmetic}"]`);
      const price = input && input.value.trim() !== '' ? Number(input.value) : NaN;
      return controller.listCosmetic(ds.cosmetic!, { currency, price });
    }
    case 'cosmetic-market-cancel': return controller.cancelCosmeticListing(ds.listing!);
    case 'cosmetic-market-buy': return controller.buyCosmeticListing(ds.listing!);

    // Merchant supplies and repair
    case 'merchant-refresh': return controller.refreshMerchant();
    case 'merchant-buy': {
      const input = appRoot.querySelector<HTMLInputElement>(`input[data-merchant-qty-for="${CSS.escape(ds.supply!)}"]`);
      const quantity = input && input.value.trim() !== '' ? Number(input.value) : 1;
      return controller.buySupply(ds.supply as 'oil' | 'torch' | 'potion', quantity);
    }
    case 'merchant-repair': return controller.repairItem(ds.item!);
    case 'merchant-salvage': return controller.salvageItem(ds.item!);
    case 'loadout-adjust': return void controller.adjustRewardLoadout(ds.supply as 'oil' | 'torch' | 'potion', Number(ds.delta));

    // waystation hub
    case 'hub-refresh': return controller.refreshPresence();
    case 'hub-emote': return controller.sendEmote(ds.emote!);
    case 'hub-inspect': return controller.inspectPlayer(ds.account!);
    case 'hub-clear-inspect': return void controller.clearInspect();

    // status
    case 'refresh-account': return controller.refreshAccount();
    case 'refresh-eligibility': return controller.refreshEligibility();
    case 'refresh-eligibility-status': return controller.refreshEligibilityStatus();
    case 'refresh-pass': return controller.refreshPass();

    // run lifecycle
    case 'start-echo': return controller.startEcho();
    case 'start-reward': return controller.startReward();
    case 'abandon-run': return controller.abandonRun();
    case 'dismiss-terminal': return void controller.dismissTerminal();

    // run actions
    case 'act-move': return controller.act(move(ds.dir as Direction));
    case 'act-wait': return controller.act(wait());
    case 'act-extract': return controller.act(extract());
    case 'act-descend': return controller.act(descend());
    case 'act-attack': return controller.act(attack(ds.target!));
    case 'act-pickup': return controller.act(pickup(ds.item!));
    case 'act-discard': return controller.act(discard(ds.item!));
    case 'act-equip-found': return controller.act(equipFound(ds.item!));
    case 'act-oil': return controller.act(useConsumable('oil'));
    case 'act-torch': return controller.act(useConsumable('torch'));
    case 'act-potion': return controller.act(useConsumable('potion'));

    // inventory / equipment
    case 'load-inventory': return controller.loadInventory();
    case 'select-inventory-item': return void controller.selectInventoryItem(ds.item!);
    case 'equip': return controller.equip(ds.item!, ds.slot as EquipSlot);
    case 'cancel-bind-equip': return void controller.cancelBindEquip();
    case 'confirm-bind-equip': return controller.confirmBindEquip();
    case 'unequip': return controller.unequip(ds.slot as EquipSlot);

    // ui
    case 'dismiss-error': return void controller.dismissError();
    default: return;
  }
}

function scrollDocsTarget(target: string | undefined): void {
  if (!target) return;
  const id = target.replace(/^#/, '');
  if (!id) return;
  window.location.hash = id;
  window.requestAnimationFrame(() => {
    appRoot.querySelector<HTMLElement>(`#${CSS.escape(id)}`)?.scrollIntoView({ block: 'start' });
  });
}

// Keep Waystation presence alive while the hub view is open. Quiet + best-effort:
// the controller no-ops unless we're authenticated and in the hall, and swallows
// any failure so a dropped heartbeat never disrupts the UI. Interval stays inside
// the backend presence TTL.
const HUB_HEARTBEAT_MS = 1_000;
setInterval(() => void controller.heartbeatHub(), HUB_HEARTBEAT_MS);

// Browsers throttle background-tab timers well past the presence TTL; heartbeat
// immediately on return so the player reappears for others without waiting.
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) void controller.heartbeatHub();
});

// Auto-play tick for an open spectate replay. No-ops unless a replay is "playing".
const SPECTATE_PLAY_MS = 850;
setInterval(() => controller.spectateTick(), SPECTATE_PLAY_MS);

// Force a fresh sign-in whenever the Phantom account drifts away from the
// wallet bound to the session (accountChanged/disconnect + focus fallback).
const walletWatcher = new WalletWatcher({
  getProvider: () => getPhantomProvider(),
  getSession: () => {
    const { session, walletKind, walletAddress } = store.get();
    return { hasSession: !!session, walletKind, walletAddress };
  },
  onWalletChanged: () => {
    controller.logoutForWalletChange();
    showToast(WALLET_CHANGED_MESSAGE, 'error');
  },
});
walletWatcher.start();

void controller.init();
